import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Loader2, Printer } from "lucide-react";
import { printClosing } from "@/lib/printShift";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";
import { prepareDurableClose, closeDurableShift, cancelDurableClose } from '@/lib/shift-journal';

const DENOMINATIONS = [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100];

export default function ShiftCloseModal({ open, onClose, onClosed, storeName, journalScope }) {
  const { approveAction, approvalDialog } = useSupervisorApproval();
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState(null);
  const [summary, setSummary] = useState(null);
  const [counts, setCounts] = useState({});
  const [other, setOther] = useState("");
  const [reason, setReason] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [varianceLimit, setVarianceLimit] = useState(20000);
  const [saving, setSaving] = useState(false);
  const dismiss = async () => {
    if (saving) return;
    try { await cancelDurableClose(journalScope); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true); setCounts({}); setOther(""); setReason(""); setRevealed(false); setShift(null); setSummary(null);
    let active = true;
    prepareDurableClose(api, journalScope).then(() => Promise.all([api.get("/shifts/summary"), api.get("/supervisor/settings")]))
      .then(([shiftResponse, securityResponse]) => {
        if (!active) return;
        setShift(shiftResponse.data.shift); setSummary(shiftResponse.data.summary);
        setVarianceLimit(Number(securityResponse.data.cash_variance_limit || 0));
      })
      .catch((error) => { if (active) { toast.error(apiError(error)); cancelDurableClose(journalScope).catch(() => {}); onClose(); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // Parent callback identities change during sync; reopening resets the form, not each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, journalScope]);

  const cash = useMemo(() => DENOMINATIONS.reduce((total, value) => total + value * Number(counts[value] || 0), 0) + Number(other || 0), [counts, other]);
  const expected = Number(summary?.cash_expected || 0);
  const difference = cash - expected;
  const denominations = DENOMINATIONS.map(value => ({ value, count: Number(counts[value] || 0) })).filter(row => row.count > 0);
  if (Number(other || 0) > 0) denominations.push({ value: 1, count: Number(other) });

  const doClose = async () => {
    if (!revealed) { setRevealed(true); return; }
    if (Math.abs(difference) > 0.009 && !reason.trim()) return toast.error("Isi alasan selisih kas");
    let approval = {};
    if (Math.abs(difference) > varianceLimit) {
      try { approval = await approveAction("close_shift_variance", `Selisih ${rupiah(difference)} · kas fisik ${rupiah(cash)}`); }
      catch (error) { return toast.error(apiError(error)); }
      if (approval === false) return;
    }
    setSaving(true);
    try {
      const data = await closeDurableShift(api, journalScope, shift, { cash_counted: cash, denominations, variance_reason: reason.trim() || null }, approval);
      toast.success("Shift ditutup"); printClosing(data, data.summary, storeName); onClosed(data);
    } catch (error) { toast.error(apiError(error)); }
    finally { setSaving(false); }
  };

  return <>
    {approvalDialog}
    <Dialog open={open} onOpenChange={value => { if (!value) dismiss(); }}>
      <DialogContent data-testid="shift-close-dialog" aria-describedby={undefined} className="bg-ui-card border-ui-line text-ui-text max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tutup Shift · Hitung Kas Fisik</DialogTitle></DialogHeader>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-readable" /></div> : summary && <div className="space-y-3">
          <p className="text-xs text-ui-muted">Tutup sif hanya selesai saat online, antrean transaksi kosong, dan kiriman Akun Kas selesai. Penutupan tidak mengirim setoran kas kedua.</p>
          <Button variant="outline" disabled={saving} onClick={async () => { setSaving(true); try { await api.post('/akunkas/sync-failed', {}); toast.success('Pemeriksaan kiriman Akun Kas selesai. Coba tutup sif.'); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); } }}>Sinkronkan Akun Kas</Button>
          <p className="text-xs text-ui-muted">Hitung uang di laci terlebih dahulu. Kas seharusnya disembunyikan agar penghitungan tidak diarahkan oleh angka sistem.</p>
          <div className="rounded-xl border border-ui-line overflow-hidden">
            <div className="grid grid-cols-[1fr_90px_120px] gap-2 px-3 py-2 bg-ui-soft text-xs font-semibold text-ui-muted"><span>Pecahan</span><span className="text-center">Lembar</span><span className="text-right">Jumlah</span></div>
            {DENOMINATIONS.map(value => <div key={value} className="grid grid-cols-[1fr_90px_120px] items-center gap-2 border-t border-ui-line px-3 py-1.5 text-sm">
              <span>{rupiah(value)}</span><Input aria-label={`Jumlah pecahan ${value}`} type="number" min="0" step="1" value={counts[value] || ""} onChange={event => setCounts(current => ({ ...current, [value]: event.target.value.replace(/\D/g, "") }))} className="h-8 text-center font-mono" /><span className="text-right font-mono">{rupiah(value * Number(counts[value] || 0))}</span>
            </div>)}
            <div className="grid grid-cols-[1fr_220px] items-center gap-2 border-t border-ui-line px-3 py-2 text-sm"><span>Koin / pecahan lain</span><MoneyInput value={other} onValueChange={setOther} className="h-9 text-right font-mono" placeholder="0" /></div>
          </div>
          <div className="flex justify-between rounded-lg bg-ui-soft px-3 py-2 font-semibold"><span>Total Kas Fisik</span><span data-testid="shift-cash-input" className="font-mono">{rupiah(cash)}</span></div>
          {revealed && <>
            <div className="rounded-xl border border-ui-line bg-ui-soft divide-y divide-ui-line text-sm">
              <Row label="Saldo Awal" value={rupiah(summary.opening_cash || shift?.opening_cash || 0)} />
              <Row label="Penjualan Tunai Bersih" value={rupiah(summary.tunai)} />
              <Row label="Pelunasan Piutang Tunai" value={rupiah(summary.pelunasan_tunai || 0)} />
              <Row label="Refund Supplier Tunai" value={rupiah(summary.supplier_cash_refunds || 0)} />
              <Row label="Kas Seharusnya" value={rupiah(expected)} strong />
            </div>
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${difference === 0 ? "bg-brand-soft text-brand-readable" : "bg-danger-soft text-danger-readable"}`}><b>Selisih</b><b data-testid="shift-selisih" className="font-mono">{rupiah(difference)}</b></div>
            {Math.abs(difference) > 0.009 && <label className="block text-xs font-semibold">Alasan Selisih<Input value={reason} onChange={event => setReason(event.target.value)} maxLength={500} placeholder="Contoh: salah memberi kembalian" className="mt-1" /></label>}
            {Math.abs(difference) > varianceLimit && <p className="text-xs text-warning-readable">Selisih melewati batas {rupiah(varianceLimit)}. PIN Supervisor akan diminta saat menutup.</p>}
          </>}
        </div>}
        <DialogFooter><Button variant="outline" disabled={saving} onClick={dismiss}>Batal</Button><Button data-testid="shift-close-confirm" onClick={doClose} disabled={saving || loading} className="bg-danger hover:bg-danger text-ui-text gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}{revealed ? "Tutup & Cetak" : "Selesai Hitung"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}

function Row({ label, value, strong = false }) {
  return <div className="flex justify-between px-3 py-1.5"><span className="text-ui-muted">{label}</span><span className={`font-mono ${strong ? "font-bold text-warning-readable" : ""}`}>{value}</span></div>;
}
